'use client';

import { useCallback, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

/**
 * Resume experience entry for PDF generation.
 * Defined locally since resume generation uses API Route pattern.
 */
export interface ResumeExperience {
  title: string;
  company?: string;
  startDate?: string;
  endDate?: string;
  description?: string;
}

interface DownloadResumeButtonProps {
  /**
   * Canonical dancer handle (professional_profiles.handle). Omit to
   * export the signed-in user's own resume.
   */
  handle?: string;
  experiences?: ResumeExperience[];
  variant?: 'default' | 'outline' | 'ghost' | 'editorial';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

interface DownloadResult {
  success: boolean;
  filename?: string;
  error?: string;
}

/**
 * Download dancer resume PDF via streaming API route
 *
 * Uses fetch with blob response type for efficient binary handling.
 * The PDF is streamed directly from the server without base64 encoding overhead.
 */
async function downloadResumePDF(options: {
  handle?: string;
  experiences?: ResumeExperience[];
}): Promise<DownloadResult> {
  const { handle, experiences } = options;

  try {
    // Build URL with query parameters
    const params = new URLSearchParams();
    if (handle) {
      params.set('handle', handle);
    }
    if (experiences && experiences.length > 0) {
      params.set('experiences', JSON.stringify(experiences));
    }

    const queryString = params.toString();
    const url = `/api/pdf/resume${queryString ? `?${queryString}` : ''}`;

    // Fetch PDF from API route
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });

    // Handle error responses
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const errorData = await response.json();
        return {
          success: false,
          error:
            errorData.error ||
            `HTTP ${response.status}: ${response.statusText}`,
        };
      }
      return {
        success: false,
        error: `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    // Get filename from Content-Disposition header
    let filename = 'resume.pdf';
    const contentDisposition = response.headers.get('content-disposition');
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) {
        filename = match[1] ?? filename;
      }
    }

    // Get blob and trigger download
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;

    if (document.body) {
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    URL.revokeObjectURL(blobUrl);

    return { success: true, filename };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to download resume',
    };
  }
}

/**
 * Button component to download dancer resume/CV as PDF
 *
 * Uses streaming API route for efficient PDF generation without base64 encoding.
 * This approach avoids hasOwnProperty errors from @react-pdf/renderer in Server Actions.
 */
export function DownloadResumeButton({
  handle,
  experiences = [],
  variant = 'outline',
  size = 'default',
  className,
}: DownloadResumeButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const { t } = useTranslation();

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);

    try {
      const result = await downloadResumePDF({
        handle,
        experiences,
      });

      if (result.success) {
        toast.success(t('dancer:publicProfile.resume.downloaded'));
      } else {
        toast.error(
          result.error || t('dancer:publicProfile.resume.generateFailed'),
        );
      }
    } catch {
      toast.error(t('dancer:publicProfile.resume.generateFailed'));
    } finally {
      setIsDownloading(false);
    }
  }, [handle, experiences, t]);

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleDownload}
      disabled={isDownloading}
      className={className}
    >
      {isDownloading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          <Trans i18nKey="dancer:publicProfile.resume.generating" />
        </>
      ) : (
        <>
          <Download className="mr-2 h-4 w-4" />
          <Trans i18nKey="dancer:publicProfile.resume.download" />
        </>
      )}
    </Button>
  );
}
